"use client";

import { cn } from "@shared/lib/cn";
import { useEffect, useState } from "react";

/**
 * Shared classnames for section enter (and optional exit) transitions.
 * Use `<RevealSection />` for the common case, or compose these for controlled
 * visibility (e.g. dismissible banners, modals).
 */
export const sectionReveal = {
  transition: "transition-[opacity,transform] duration-300 ease-out",
  hidden: "translate-y-1 opacity-0",
  visible: "translate-y-0 opacity-100",
} as const;

const DEFAULT_ENTER_AFTER_MS = 50;

type RevealSectionProps = {
  children: React.ReactNode;
  className?: string;
  /** Extra delay after mount before animating in (ms). Stagger sections with 40, 80, 120, … */
  delayMs?: number;
};

/**
 * Wraps a block so it fades and slides slightly into place on mount, consistent across pages.
 */
export const RevealSection = ({ children, className, delayMs = 0 }: RevealSectionProps) => {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(() => setShown(true), DEFAULT_ENTER_AFTER_MS + delayMs);
    return () => window.clearTimeout(id);
  }, [delayMs]);

  return (
    <div className={cn(sectionReveal.transition, shown ? sectionReveal.visible : sectionReveal.hidden, className)}>
      {children}
    </div>
  );
};
